import type { FeatureFlagKey, FeatureFlagScope } from '@dosfilos/domain';
import { callable } from '../callable';

// ─── Types (mirror apps/functions/src/admin/platform.functions.ts) ────────────

export type TenantStatus = 'active' | 'suspended' | 'archived';

export interface TenantOverviewRow {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  createdAt: string | null;
  counts: {
    students: number;
    courses: number;
    sections: number;
    enrollments: number;
  };
  storageBytes: number;
  lastActivityAt: string | null;
}

export interface PlatformOverview {
  generatedAt: string;
  tenants: TenantOverviewRow[];
  totals: {
    tenants: number;
    active: number;
    suspended: number;
    archived: number;
    students: number;
    courses: number;
    storageBytes: number;
  };
  /** Last AI provider billing incident (credit exhausted): since when, how many
   *  failures and in which functions. Null if there never was one. */
  aiBillingIncident?: {
    firstAt: string;
    lastAt: string;
    count: number;
    byFunction: Record<string, number>;
  } | null;
}

export interface TenantInviteRow {
  token: string;
  email: string;
  tenantId: string;
  tenantName: string;
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'expired' | 'used';
}

export interface PlatformAuditRow {
  id: string;
  action: string;
  actorEmail: string;
  tenantId: string;
  tenantName: string;
  details: Record<string, unknown> | null;
  at: string | null;
}

export interface TenantDetail {
  tenantId: string;
  setup: {
    stages: { key: string; steps: { key: string; optional: boolean; done: boolean }[] }[];
    essentialDone: number;
    essentialTotal: number;
    pct: number;
  };
  health: { key: string; ok: boolean }[];
  activity: { lastActivityAt: string | null; areas: { area: string; at: string | null }[] };
  /** Mirror of `TenantDetail.flags` in apps/functions/src/admin/platform.functions.ts. */
  flags: {
    isDemo: boolean;
    features: {
      key: FeatureFlagKey;
      scope: FeatureFlagScope;
      enabled: boolean;
      explicit: boolean;
    }[];
  };
}

export type PlatformFlag = FeatureFlagKey | 'isDemo';

export type UserInviteState = 'pending' | 'expired' | 'used' | 'superseded';

/** Mirror of `UserActivity` in apps/functions/src/admin/platform.functions.ts. */
export interface UserActivity {
  uid: string;
  email: string | null;
  displayName: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
  disabled: boolean;
  activated: boolean;
  /** `null` with `activated` = activated before the date was recorded. */
  activatedAt: string | null;
  memberships: Array<{ tenantId: string; tenantName: string; roles: string[]; status: string }>;
  invites: Array<{
    purpose: 'activation' | 'password-reset';
    tenantId: string;
    tenantName: string;
    state: UserInviteState;
    createdAt: string | null;
    expiresAt: string | null;
    usedAt: string | null;
  }>;
  recentActions: Array<{
    tenantId: string;
    tenantName: string;
    action: string;
    targetCollection: string;
    at: string;
  }>;
}

export const platformApi = {
  getOverview: async (): Promise<PlatformOverview> => {
    const fn = callable<Record<string, never>, PlatformOverview>('getPlatformOverview');
    return (await fn({})).data;
  },

  setTenantStatus: async (tenantId: string, status: TenantStatus) => {
    const fn = callable<
      { tenantId: string; status: TenantStatus },
      { ok: true; tenantId: string; status: TenantStatus }
    >('setTenantStatus');
    return (await fn({ tenantId, status })).data;
  },

  logImpersonation: async (tenantId: string, tenantName: string, phase: 'start' | 'stop') => {
    const fn = callable<
      { tenantId: string; tenantName: string; phase: 'start' | 'stop' },
      { ok: true }
    >('logImpersonation');
    return (await fn({ tenantId, tenantName, phase })).data;
  },

  listInvites: async (): Promise<{ invites: TenantInviteRow[] }> => {
    const fn = callable<Record<string, never>, { invites: TenantInviteRow[] }>('listTenantInvites');
    return (await fn({})).data;
  },

  resendInvite: async (email: string, tenantId: string, displayName?: string) => {
    const fn = callable<{ email: string; tenantId: string; displayName?: string }, { ok: true }>(
      'resendTenantInvite',
    );
    return (await fn({ email, tenantId, ...(displayName ? { displayName } : {}) })).data;
  },

  /** La actividad de una cuenta (solo plataforma; la consulta queda auditada). */
  getUserActivity: async (uid: string): Promise<UserActivity> => {
    const fn = callable<{ uid: string }, UserActivity>('getUserActivity');
    return (await fn({ uid })).data;
  },

  listAudit: async (): Promise<{ events: PlatformAuditRow[] }> => {
    const fn = callable<Record<string, never>, { events: PlatformAuditRow[] }>(
      'listPlatformAuditEvents',
    );
    return (await fn({})).data;
  },

  setTenantPlatformFlag: async (tenantId: string, flag: PlatformFlag, enabled: boolean) => {
    const fn = callable<
      { tenantId: string; flag: PlatformFlag; enabled: boolean },
      { ok: true; tenantId: string; flag: PlatformFlag; enabled: boolean }
    >('setTenantPlatformFlag');
    return (await fn({ tenantId, flag, enabled })).data;
  },

  getTenantDetail: async (tenantId: string): Promise<TenantDetail> => {
    const fn = callable<{ tenantId: string }, TenantDetail>('getTenantDetail');
    return (await fn({ tenantId })).data;
  },

  seedStarter: async (tenantId: string): Promise<{ ok: true; applied: string[] }> => {
    const fn = callable<{ tenantId: string }, { ok: true; applied: string[] }>(
      'seedStarterContent',
    );
    return (await fn({ tenantId })).data;
  },
};
